// iter-121.117 — OA "For Your Information" tabs
//
// 3 tabs (Business / Usage & AI / Reliability), each rendering 4 KPI
// cards + a Top-10 Workspaces ranking table for the tab's primary
// metric.
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useOaScope } from "@/lib/oa-scope";
import KpiCard from "./KpiCard";
import WhatsNewWidget from "../WhatsNewWidget";

const TABS = [
    { value: "business",    label: "Business",    metric: "users" },
    { value: "usage_ai",    label: "Usage & AI",  metric: "ai_spend" },
    { value: "reliability", label: "Reliability", metric: "queries" },
    { value: "whats_new",   label: "What's new" },
];

function MetricLabel({ metric }) {
    const map = {
        ai_spend: "Top 10 Workspaces by AI Spend",
        queries:  "Top 10 Workspaces by Queries",
        users:    "Top 10 Workspaces by Active Users",
        datasets: "Top 10 Workspaces by Datasets",
    };
    return <span>{map[metric] || metric}</span>;
}

function TopWorkspacesTable({ metric, queryString }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        setRows(null);
        api.get(`/home/oa/top-workspaces?metric=${metric}&limit=10&${queryString}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [metric, queryString]);

    const valueFormat = useMemo(() => {
        if (metric === "ai_spend") {
            return (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        }
        return (n) => Number(n).toLocaleString();
    }, [metric]);

    if (rows === null) {
        return <div className="text-xs text-muted-foreground py-6 text-center">Loading top workspaces…</div>;
    }
    if (rows.length === 0) {
        return <div className="text-xs text-muted-foreground py-6 text-center"
                    data-testid={`oa-top-ws-empty-${metric}`}>No workspace activity in the selected scope.</div>;
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden"
             data-testid={`oa-top-ws-table-${metric}`}>
            <div className="px-3 py-2 bg-secondary/30 flex items-center justify-between border-b border-border">
                <div className="eyebrow text-[10px]"><MetricLabel metric={metric} /></div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {rows.length} {rows.length === 1 ? "workspace" : "workspaces"}
                </div>
            </div>
            <table className="w-full text-[12px]">
                <thead className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="text-left px-3 py-1.5 w-8">#</th>
                        <th className="text-left px-3 py-1.5">Workspace</th>
                        <th className="text-right px-3 py-1.5">Value</th>
                        <th className="text-right px-3 py-1.5 hidden sm:table-cell">Secondary</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.workspace_id} className="border-t border-border"
                            data-testid={`oa-top-ws-row-${metric}-${i}`}>
                            <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-1.5 font-medium">{r.workspace_name}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                {valueFormat(r.value)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground hidden sm:table-cell">
                                {r.secondary
                                    ? `${(r.secondary.value || 0).toLocaleString()} ${r.secondary.label}`
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TabBody({ tab, metric }) {
    const { queryString } = useOaScope();
    const [cells, setCells] = useState(null);

    useEffect(() => {
        setCells(null);
        api.get(`/home/oa/kpi-hero?tab=${tab}&${queryString}`)
            .then((r) => setCells(r.data?.cells || []))
            .catch(() => setCells([]));
    }, [tab, queryString]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                 data-testid={`oa-info-kpi-grid-${tab}`}>
                {cells === null
                    ? [1, 2, 3, 4].map((i) => <div key={i} className="h-32 shimmer rounded-sm" />)
                    : cells.map((c) => <KpiCard key={c.key} cell={c} />)}
            </div>
            <TopWorkspacesTable metric={metric} queryString={queryString} />
        </div>
    );
}

export default function OaInfoTabs({ onEmpty }) {
    const [tab, setTab] = useState("business");
    useEffect(() => { onEmpty && onEmpty(false); }, [onEmpty]);
    return (
        <div data-testid="oa-info-tabs">
            <div className="eyebrow text-[10px] mb-2">For Your Information</div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="oa-info-tabs-list">
                    {TABS.map((t) => (
                        <TabsTrigger key={t.value} value={t.value}
                                     data-testid={`oa-info-tab-${t.value}`}>
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {TABS.map((t) => (
                    <TabsContent key={t.value} value={t.value} className="mt-3"
                                 data-testid={`oa-info-tab-content-${t.value}`}>
                        {tab === t.value && (t.value === "whats_new"
                            ? <WhatsNewWidget />
                            : <TabBody tab={t.value} metric={t.metric} />)}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
